import React, { useEffect, useState } from 'react';
import { CalendarCheck, CalendarX, Loader2, ShieldAlert } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { verificarLlegada } from '../services/workedHours.services';

// Verifica contra la planificación de turnos si el empleado puede marcar la llegada ahora,
// según la restricción del centro (apagado / aviso / bloqueo).
export const useVerificacionLlegada = (laundryId, employee) => {
  const [verificacion, setVerificacion] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setVerificacion(null);
    setError(null);
    if (!laundryId || !employee) return undefined;
    let vigente = true;
    setCargando(true);
    verificarLlegada({ laundryId, employeeId: employee._id, cedula: employee.cedula })
      .then((v) => vigente && setVerificacion(v))
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [laundryId, employee]);

  return { verificacion, cargando, error };
};

// ¿Se debe impedir el registro? Solo en bloqueo y sin planificación (salvo excepción con motivo).
export const registroBloqueado = (verificacion, excepcion) =>
  !!verificacion &&
  verificacion.modo === 'bloqueo' &&
  !verificacion.planificado &&
  !(excepcion?.activa && excepcion.motivo.trim());

/**
 * Recuadro con el resultado de la verificación. Si `permiteExcepcion`, en bloqueo muestra la
 * opción de registrar por excepción con motivo (solo admin/supervisor).
 */
const PlanificacionLlegada = ({ verificacion, cargando, error, permiteExcepcion = false, excepcion, onExcepcionChange }) => {
  if (cargando) {
    return (
      <div className="flex items-center gap-2 rounded-md border p-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Verificando la planificación de turnos…
      </div>
    );
  }
  if (error) {
    return <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-200">No se pudo verificar la planificación: {error}</div>;
  }
  if (!verificacion) return null;

  const { modo, planificado, mensaje } = verificacion;

  if (planificado) {
    return (
      <div className="flex items-start gap-2 rounded-md border-l-4 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm text-emerald-800 dark:text-emerald-200">
        <CalendarCheck className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{mensaje}</span>
      </div>
    );
  }

  // Sin planificación y restricción apagada: solo informativo
  if (modo === 'apagado') {
    return (
      <div className="flex items-start gap-2 rounded-md border p-3 text-sm text-muted-foreground">
        <CalendarX className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{mensaje}</span>
      </div>
    );
  }

  if (modo === 'aviso') {
    return (
      <div className="flex items-start gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-200">
        <CalendarX className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">Llegada sin planificar</p>
          <p>{mensaje} Puede registrarse, pero quedará marcada como llegada sin planificar.</p>
        </div>
      </div>
    );
  }

  // Bloqueo
  return (
    <div className="space-y-3 rounded-md border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-800 dark:text-red-200">
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">No puede registrar la llegada</p>
          <p>{mensaje} El centro solo permite marcar a los empleados planificados.</p>
        </div>
      </div>
      {permiteExcepcion && (
        <div className="space-y-2 rounded border border-red-200 dark:border-red-900 bg-white/60 dark:bg-black/20 p-2.5 text-foreground">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <Checkbox
              checked={!!excepcion?.activa}
              onCheckedChange={(v) => onExcepcionChange({ ...excepcion, activa: !!v })}
            />
            Registrar por excepción
          </label>
          {excepcion?.activa && (
            <div className="space-y-1">
              <Label htmlFor="motivo-excepcion" className="text-xs">Motivo (obligatorio)</Label>
              <Textarea
                id="motivo-excepcion"
                rows={2}
                placeholder="Ej.: reemplazo de un compañero incapacitado, turno cambiado sin actualizar la planificación…"
                value={excepcion.motivo}
                onChange={(e) => onExcepcionChange({ ...excepcion, motivo: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground">La llegada quedará marcada como excepción con tu usuario y el motivo.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanificacionLlegada;
